/**
 * Admin HTTP request wrapper.
 */
import { clearAdminSession, getAdminAccessToken, isAdminSessionExpired } from './admin-auth';
import { getAccessToken as getLogtoAccessToken } from './tokenBridge';
import { isLogtoEnabled } from '@/providers/LogtoProviderWrapper';
import { getBaseUrl } from './utils';

/**
 * Get the appropriate access token based on auth mode
 */
async function getAuthToken(): Promise<string> {
  if (isLogtoEnabled()) {
    // Use Logto token
    const token = await getLogtoAccessToken();
    return token || '';
  }
  // Use legacy admin token
  return getAdminAccessToken();
}

async function doRequest<T>(
  method: string,
  url: string,
  data?: any,
  params?: Record<string, any>,
  signal?: AbortSignal
): Promise<T> {
  // Only check legacy session expiry when not using Logto
  if (!isLogtoEnabled() && isAdminSessionExpired()) {
    clearAdminSession();
    window.location.href = '/app/admin';
    throw new Error('Session expired');
  }

  const baseUrl = getBaseUrl();
  let fullUrl = `${baseUrl}${url}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) searchParams.set(k, String(v));
    });
    const qs = searchParams.toString();
    if (qs) fullUrl += '?' + qs;
  }

  const token = await getAuthToken();
  const options: RequestInit = {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal,
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(fullUrl, options);

  if (response.status === 401) {
    clearAdminSession();
    window.location.href = '/app/admin';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export const adminGet = <T>(
  url: string,
  params?: Record<string, any>,
  options?: { signal?: AbortSignal }
) => doRequest<T>('GET', url, undefined, params, options?.signal);

export const adminPost = <T>(url: string, data?: any) =>
  doRequest<T>('POST', url, data);

export const adminPut = <T>(url: string, data?: any) =>
  doRequest<T>('PUT', url, data);

export const adminDelete = <T>(url: string) =>
  doRequest<T>('DELETE', url);

export async function adminUpload<T>(
  url: string,
  files: File[],
  fieldName: string = 'files',
  onProgress?: (percent: number) => void
): Promise<T> {
  // Only check legacy session expiry when not using Logto
  if (!isLogtoEnabled() && isAdminSessionExpired()) {
    clearAdminSession();
    window.location.href = '/app/admin';
    throw new Error('Session expired');
  }

  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${url}`;
  const token = await getAuthToken();

  const formData = new FormData();
  files.forEach((file) => formData.append(fieldName, file));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', fullUrl);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status === 401) {
        clearAdminSession();
        window.location.href = '/app/admin';
        reject(new Error('Unauthorized'));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Invalid JSON response'));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.detail || `HTTP ${xhr.status}`));
        } catch {
          reject(new Error(`HTTP ${xhr.status}`));
        }
      }
    };

    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });
}
